import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@blis/trpc/react";

import { TestSpecLabel } from "@/components/TestSpecLabel";
import { Button } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Description, FieldError, Label } from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import type { TSample } from "@/lib/types";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import { useDialogProps } from "../detail/state";
import { BaseTestResultBadge } from "./TestResultDetailsDialog";

const formSchema = z.object({
  sampleId: z.number(),
  instrumentRef: z.string().optional(),
  results: z.number().array(),
});
type FormValues = z.infer<typeof formSchema>;

type ResultsDialogProps = {
  accession: { id: number };
  samples: TSample[];
};

export function ResultsDialog({ samples }: ResultsDialogProps) {
  const { open, onOpenChange } = useDialogProps("results");
  const { control, handleSubmit, setValue, register } = useForm<FormValues>({
    defaultValues: {
      sampleId: samples.length === 1 ? samples[0].id : undefined,
      results: [],
    },
    resolver: zodResolver(formSchema),
  });

  const { mutate, isPending } = trpc.results.bulkEnter.useMutation(
    mutationResponse({
      successMessage: "Results entered and validated",
      errorTitle: "Error submitting results",
      onSuccess: () => {
        onOpenChange?.(null);
      },
    }),
  );

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const results = open?.results.filter((r) =>
      data.results.includes(r.testOrderResult.id),
    );
    if (!results || results.length === 0) {
      // TODO: set and show error
      return;
    }

    mutate({
      results: results
        .filter((r) => r.hlaResult.length >= 1)
        .map(({ hlaResult, testOrderResult }) => {
          return {
            id: testOrderResult.id,
            sampleId: data.sampleId,
            resultHla: hlaResult,
          };
        }),
      validate: true,
      instrumentRef: data.instrumentRef,
    });
  };

  useEffect(() => {
    if (open) {
      setValue(
        "results",
        open.results
          .filter((r) => r.hlaResult.length >= 1)
          .map((r) => r.testOrderResult.id),
      );
    }
  }, [open, setValue]);

  useEffect(() => {
    if (samples.length === 1) {
      setValue("sampleId", samples[0].id);
    }
  }, [samples, setValue]);

  const testSpec = open?.testOrder.testSpec;

  return (
    <Modal isOpen={!!open} onOpenChange={() => onOpenChange?.(null)}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Upload Results</DialogTitle>
        </DialogHeader>
        <DialogBody className="gap-4">
          <form
            id="results-form"
            className="contents"
            onSubmit={handleSubmit(onSubmit)}
          >
            {testSpec?.name && (
              <div className="flex flex-col mb-2 gap-1">
                <Text elementType="p" muted>
                  Parsed results for:
                </Text>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <TestSpecLabel name={testSpec.name} code={testSpec.code} />
                  {open?.instrument && testSpec.instrument && (
                    <Text size="2" muted>
                      from {testSpec.instrument.model}
                    </Text>
                  )}
                </div>
                {open?.instrument && (
                  <input
                    type="hidden"
                    value={open.instrument}
                    {...register("instrumentRef")}
                  />
                )}
              </div>
            )}

            <Controller
              control={control}
              name="sampleId"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <ComboBox
                  {...field}
                  menuTrigger="focus"
                  onSelectionChange={(evt) => field.onChange(evt)}
                  selectedKey={field.value ?? null}
                  validationBehavior="aria"
                  isInvalid={!!error}
                >
                  <Label>Sample used</Label>
                  <ComboBoxInput ref={ref} />
                  <ComboBoxList items={samples}>
                    {(option) => (
                      <ComboBoxItem
                        key={option.id}
                        id={option.id}
                        textValue={option.tube?.name}
                      >
                        <div className="flex flex-col gap-0">
                          <Text slot="label">{option.tube?.name}</Text>
                          <Description className="text-muted-foreground/80 text-xs">
                            {format(option.collection)}
                          </Description>
                        </div>
                      </ComboBoxItem>
                    )}
                  </ComboBoxList>
                  <FieldError>{error?.message}</FieldError>
                </ComboBox>
              )}
            />
            <Controller
              control={control}
              name="results"
              render={({ field }) => (
                <GridList
                  aria-label="Results"
                  items={open?.results || []}
                  selectionMode="multiple"
                  onSelectionChange={(keys) => {
                    const allResults = open?.results
                      .filter((r) => r.hlaResult.length >= 1)
                      .map((r) => r.testOrderResult.id);

                    if (keys === "all") {
                      field.onChange(allResults);
                    } else {
                      field.onChange(Array.from(keys));
                    }
                  }}
                  selectedKeys={field.value ?? []}
                  disabledKeys={open?.results
                    .filter((r) => !r.hlaResult.length)
                    .map((r) => r.testOrderResult.id)}
                >
                  {(result) => (
                    <GridListItem
                      key={result.testOrderResult.id}
                      id={result.testOrderResult.id}
                      className="w-full"
                      textValue={
                        result.testOrderResult.testResultSpec.name ?? ""
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        {result.testOrderResult.testResultSpec.name}
                        <div className="flex items-center gap-2">
                          {result.hlaResult && result.hlaResult.length > 0 ? (
                            <BaseTestResultBadge result={result.hlaResult} />
                          ) : (
                            <Text muted size="xs">
                              No result
                            </Text>
                          )}
                        </div>
                      </div>
                    </GridListItem>
                  )}
                </GridList>
              )}
            />
          </form>
        </DialogBody>
        <DialogFooter>
          <Button type="submit" form="results-form" isPending={isPending}>
            Approve
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
